import { ref, computed } from 'vue';

/**
 * Composable que gestiona la selección y filtrado de ofertas de plataforma.
 *
 * @param {Ref<Array>} offers - Lista reactiva de ofertas normalizadas
 * @param {Ref<Array>} states - Lista reactiva de estados geográficos
 */
export function usePlatformOffers(offers, states) {
  const selectedOfferId = ref(null);
  const selectedState = ref(null);

  /**
   * Lista de estados que tienen al menos una oferta activa, más la opción "Todos".
   */
  const statesWithOffers = computed(() => {
    const stateIdsInOffers = new Set();
    offers.value.forEach((offer) => {
      const stateId = offer.stateId || offer.state_id;
      if (stateId) {
        stateIdsInOffers.add(Number(stateId));
      }
    });

    const allOption = { id: null, name: 'Todos' };
    const stateList = states?.value || [];
    const filteredStates = stateList.filter((state) =>
      stateIdsInOffers.has(Number(state.id))
    );

    return [allOption, ...filteredStates];
  });

  /**
   * Ofertas filtradas por el estado seleccionado, ordenadas por nombre de ciudad.
   */
  const filteredOffers = computed(() => {
    const sortByCityName = (list) =>
      [...list].sort((a, b) => {
        const aName = (a?.cityName || '').toString().trim();
        const bName = (b?.cityName || '').toString().trim();
        if (!aName && !bName) return 0;
        if (!aName) return 1;
        if (!bName) return -1;
        return aName.localeCompare(bName, 'es', { sensitivity: 'base' });
      });

    if (!selectedState.value?.id) {
      return sortByCityName(offers.value);
    }

    const selectedStateId = selectedState.value.id;
    const filtered = offers.value.filter((offer) => {
      if (offer.stateId || offer.state_id) {
        const offerStateId = offer.stateId || offer.state_id;
        return (
          offerStateId == selectedStateId ||
          Number(offerStateId) === Number(selectedStateId)
        );
      }
      if (offer.stateName && selectedState.value.name) {
        return (
          offer.stateName.toLowerCase() === selectedState.value.name.toLowerCase()
        );
      }
      return true;
    });

    return sortByCityName(filtered);
  });

  /**
   * Monto efectivo de la oferta seleccionada considerando preventas vigentes.
   * Devuelve 0 si no hay oferta seleccionada.
   */
  const getCurrentPaymentAmount = computed(() => {
    if (!selectedOfferId.value) return 0;

    const offer = offers.value.find((o) => o.id === selectedOfferId.value);
    if (!offer) return 0;

    const now = new Date();

    if (offer.presale_1_start_date && offer.presale_1_end_date) {
      const p1Start = new Date(offer.presale_1_start_date);
      const p1End = new Date(offer.presale_1_end_date);
      if (now >= p1Start && now <= p1End && Number(offer.presale_1_amount || 0) > 0) {
        return offer.presale_1_amount;
      }
    }

    if (offer.presale_2_start_date && offer.presale_2_end_date) {
      const p2Start = new Date(offer.presale_2_start_date);
      const p2End = new Date(offer.presale_2_end_date);
      if (now >= p2Start && now <= p2End && Number(offer.presale_2_amount || 0) > 0) {
        return offer.presale_2_amount;
      }
    }

    return offer.amount;
  });

  /**
   * Establece la oferta seleccionada por ID.
   * @param {string|number} offerId
   */
  const selectOffer = (offerId) => {
    selectedOfferId.value = offerId;
  };

  return {
    selectedOfferId,
    selectedState,
    statesWithOffers,
    filteredOffers,
    getCurrentPaymentAmount,
    selectOffer,
  };
}
